import { forwardRef, HTMLAttributes, useCallback, useRef } from 'react'
import { TransitionStatus } from 'react-transition-group'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Button from '@/components/base/button'
import Typography from '@/components/base/typography'
import LanguageList from '@/components/layout/language-list'
import { useStore } from '@/contexts/store'
import { stripSlash } from '@/utils/path'

import { cn } from '@/lib/utils'

export type MenuProps = HTMLAttributes<HTMLDivElement> & {
  state: TransitionStatus
}

const Menu = forwardRef<HTMLDivElement, MenuProps>(
  ({ state, className, ...props }, ref) => {
    const { asPath } = useRouter()
    const { menu } = useStore()
    const panel = useRef<HTMLDivElement>(null!)

    const isActive = useCallback(
      (url: string) => stripSlash(url) === stripSlash(`/${asPath}`),
      [asPath]
    )

    return (
      <div
        ref={ref}
        className={cn(
          'static md:hidden',
          'before:fixed before:left-0 before:top-0 before:z-menu before:h-full before:w-full before:bg-section-backgroud-light',
          'before:duration-600 before:transition-opacity before:ease-in-out-cubic',
          {
            'before:opacity-0 before:duration-0': state === 'entering',
            'before:opacity-100': state === 'entered',
            'before:opacity-0 before:ease-out-cubic': state === 'exiting'
          }
        )}
      >
        <div
          ref={panel}
          className={cn(
            'fixed left-0 top-0 z-menu flex h-full w-full lg:w-auto',
            'duration-600 transition-opacity ease-in-out-cubic',
            {
              'opacity-0 duration-0': state === 'entering',
              'opacity-100': state === 'entered',
              'opacity-0 ease-out-cubic': state === 'exiting'
            },
            className
          )}
          {...props}
        >
          <div className="flex grow flex-col justify-center gap-[4rem] px-margin pb-[1.6rem] pt-header-height">
            <nav className="grid text-center">
              <ul className="flex flex-col items-center justify-center gap-y-[2.4rem]">
                {menu?.header_menu_01?.menu.map(({ url, title }, index) => (
                  <li
                    key={`header-menu-01-${index}`}
                    className="overflow-hidden border-b-[1px] border-dashed border-b-text-secondary-dark px-1 pb-[2.4rem]"
                  >
                    <Link
                      href={url}
                      scroll={false}
                      className={cn(
                        'block text-text-heading-light',
                        'transition-colors duration-300 ease-out-cubic',
                        {
                          'pointer-events-none text-text-accent-light':
                            isActive(url)
                        }
                      )}
                    >
                      <Typography variant="menu-mobile">{title}</Typography>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <nav>
              <ul className="flex flex-col items-center justify-center gap-y-[2.4rem]">
                {menu?.header_menu_02?.menu.map(
                  ({ url, title, target }, index) => (
                    <li key={`header-menu-02-${index}`}>
                      <Button asChild variant="accent" isLiquid>
                        <Link href={url} target={target} scroll={false}>
                          <Typography variant="regular">{title}</Typography>
                        </Link>
                      </Button>
                    </li>
                  )
                )}
              </ul>
            </nav>
            <div className="flex justify-center">
              <LanguageList itemClassName="text-text-heading-light" />
            </div>
          </div>
        </div>
      </div>
    )
  }
)

export default Menu
